import React, { useCallback, useEffect, useRef, useState } from 'react'
import { connect } from 'react-redux'
import {
    Button, Chip, Tooltip, IconButton, TextField, MenuItem,
    Avatar, CircularProgress, TablePagination, InputAdornment, Tabs, Tab,
} from '@material-ui/core'
import { makeStyles, withStyles } from '@material-ui/core/styles'
import LinearProgress from '@material-ui/core/LinearProgress'
import Skeleton from '@material-ui/lab/Skeleton'

import NotificationsNoneIcon from '@material-ui/icons/NotificationsNone'
import NotificationsActiveIcon from '@material-ui/icons/NotificationsActive'
import DoneAllIcon from '@material-ui/icons/DoneAll'
import DraftsIcon from '@material-ui/icons/Drafts'
import MarkunreadIcon from '@material-ui/icons/Markunread'
import InboxIcon from '@material-ui/icons/Inbox'
import SearchIcon from '@material-ui/icons/Search'
import ClearIcon from '@material-ui/icons/Close'
import RefreshIcon from '@material-ui/icons/Refresh'
import FilterListIcon from '@material-ui/icons/FilterList'
import AttachFileIcon from '@material-ui/icons/AttachFile'
import OpenInNewIcon from '@material-ui/icons/OpenInNew'
import SearchOffIcon from '@material-ui/icons/FindInPage'
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline'
import AccessTimeIcon from '@material-ui/icons/AccessTime'
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown'

import moment from 'moment'
import HttpService from '../../services/httpService'
import { setSnackInfo } from '../../redux/actions/appAction'
import NotificationDetailSheet, { toneOf } from '../../components/NotificationDetailSheet'

const ORANGE = '#F47E44'
const ORANGE_DARK = '#c1531f'
const ORANGE_LIGHT = '#fff1ea'
const HEADER_GRADIENT = `linear-gradient(135deg, ${ORANGE_DARK} 0%, ${ORANGE} 60%, #ff9c66 100%)`
const INK = '#3d2b21'
const RULE = '#eceff3'

const ROWS_OPTIONS = [10, 25, 50]
const ALL_CATEGORIES = '__all__'

// yyyy-mm-dd from LOCAL parts. toISOString() would shift an evening in IST
// back to the previous day, so "Today" would miss everything sent after 18:30.
const isoDay = (d) => [
    d.getFullYear(),
    String(d.getMonth() + 1).padStart(2, '0'),
    String(d.getDate()).padStart(2, '0'),
].join('-')

const QUICK_RANGES = [
    { label: 'Today', range: () => ({ from: isoDay(new Date()), to: isoDay(new Date()) }) },
    { label: 'Last 7 days', range: () => ({ from: isoDay(new Date(Date.now() - 6 * 864e5)), to: isoDay(new Date()) }) },
    { label: 'Last 30 days', range: () => ({ from: isoDay(new Date(Date.now() - 29 * 864e5)), to: isoDay(new Date()) }) },
]

const TABS = [
    { key: 'all', label: 'All', Icon: InboxIcon },
    { key: 'unread', label: 'Unread', Icon: MarkunreadIcon },
    { key: 'read', label: 'Read', Icon: DraftsIcon },
]

const BrandLinearProgress = withStyles(() => ({
    root: { height: 4, borderRadius: 999, backgroundColor: 'rgba(193,83,31,0.10)' },
    bar: { borderRadius: 999, background: `linear-gradient(90deg, ${ORANGE_DARK}, ${ORANGE}, #ff9c66)` },
}))(LinearProgress)

const useStyles = makeStyles((theme) => ({
    // Sized to the viewport so the pager is in view on load instead of being
    // something you scroll down to find. 144px is the app chrome above: antd
    // Header 64 + Content margin 16*2 + Content padding 24*2 (sidebar.js).
    root: {
        height: 'calc(100vh - 144px)',
        boxSizing: 'border-box',
        display: 'flex', flexDirection: 'column',
        // auto, NOT hidden: every link below is flex:1 + minHeight:0, so
        // children squash rather than overflow. Once the chrome alone exceeds
        // the viewport - which browser zoom causes easily - hidden would cut
        // the list off with no way to reach it.
        overflow: 'auto',
        [theme.breakpoints.down('xs')]: { height: 'auto', minHeight: '100vh', overflow: 'visible', paddingBottom: 32 },
        // Width breakpoints cannot see zoom: a zoomed 1920px monitor is still
        // 'md' wide, so height needs a ladder of its own.
        '@media (max-height: 640px)': { height: 'auto', minHeight: 0, overflow: 'visible', paddingBottom: 32 },
    },

    banner: {
        display: 'flex', alignItems: 'center', gap: 16,
        padding: '14px 22px', borderRadius: 16,
        background: HEADER_GRADIENT, color: '#fff',
        boxShadow: '0 8px 24px rgba(93,55,28,0.18)', marginBottom: 14, flexShrink: 0,
        [theme.breakpoints.down('xs')]: { padding: '14px 16px', flexWrap: 'wrap' },
        '@media (max-height: 820px)': { padding: '8px 16px', marginBottom: 8, borderRadius: 12, gap: 12 },
    },
    bannerIcon: {
        display: 'grid', placeItems: 'center', width: 44, height: 44,
        borderRadius: 14, background: 'rgba(255,255,255,0.18)', flexShrink: 0,
        '@media (max-height: 820px)': { width: 34, height: 34, borderRadius: 10 },
    },
    bannerTitle: {
        fontSize: 19, fontWeight: 700, lineHeight: 1.3,
        '@media (max-height: 820px)': { fontSize: 16 },
    },
    bannerSub: {
        fontSize: 12.5, color: 'rgba(255,255,255,0.88)', marginTop: 3,
        '@media (max-height: 820px)': { display: 'none' },
    },
    bannerActions: { marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
    whiteBtn: {
        background: '#fff', color: ORANGE_DARK, fontWeight: 700,
        borderRadius: 10, textTransform: 'none', padding: '6px 16px',
        boxShadow: '0 4px 14px rgba(0,0,0,0.15)',
        '&:hover': { background: ORANGE_LIGHT },
        '&.Mui-disabled': { background: 'rgba(255,255,255,0.8)', color: ORANGE_DARK, boxShadow: 'none' },
    },
    spin: { animation: '$spin 900ms linear infinite' },
    '@keyframes spin': { from: { transform: 'rotate(0deg)' }, to: { transform: 'rotate(360deg)' } },

    progressSlot: { height: 4, marginBottom: 10, flexShrink: 0 },
    loadingNote: {
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '7px 13px', marginBottom: 12, borderRadius: 12,
        background: ORANGE_LIGHT, border: `1px solid ${ORANGE}33`, flexShrink: 0,
        '@media (max-height: 820px)': { padding: '5px 11px', marginBottom: 8 },
    },
    loadingNoteTitle: { fontSize: 12.5, fontWeight: 700, color: ORANGE_DARK },
    loadingNoteHint: { fontSize: 11.5, color: '#7a6a60', marginTop: 1 },

    // The antd Content wrapper is the scroll container (overflow:auto in
    // sidebar.js), so top:0 pins this to the top of the page area.
    //
    // The negative margins and matching padding are load-bearing: without them
    // the rows would show through the 16px gutter either side of the sticky
    // block as they scroll past it.
    // Pinned by the flex layout rather than position:sticky - the LIST is the
    // scroller now, so the head simply never moves.
    stickyHead: {
        flexShrink: 0,
        background: '#fff',
        margin: '0 -16px', padding: '6px 16px 0',
        borderBottom: `1px solid ${RULE}`,
    },

    card: {
        flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden',
        borderRadius: 16, background: '#fff',
        border: `1px solid ${RULE}`, boxShadow: '0 4px 24px rgba(93,55,28,0.08)',
        padding: '6px 16px 0',
        // The floor. With minHeight:0 the list shrinks to nothing and the root
        // never overflows, so root's `auto` would never show a scrollbar.
        minHeight: 300,
        [theme.breakpoints.down('xs')]: { flex: 'none', overflow: 'visible', minHeight: 0 },
        '@media (max-height: 640px)': { flex: 'none', overflow: 'visible', minHeight: 0, paddingBottom: 12 },
    },

    tabs: { minHeight: 46, borderBottom: `1px solid ${RULE}`, marginBottom: 12 },
    tab: {
        minHeight: 46, minWidth: 128, textTransform: 'none',
        fontWeight: 700, fontSize: 13.5, color: '#8a95a6',
        '& .MuiTab-wrapper': { flexDirection: 'row', gap: 8 },
        '&.Mui-selected': { color: ORANGE_DARK },
    },
    tabIndicator: { backgroundColor: ORANGE_DARK, height: 3, borderRadius: 3 },
    tabCount: {
        fontSize: 10.5, fontWeight: 800, borderRadius: 999, padding: '1px 7px',
        background: '#eef1f5', color: '#4f596a',
    },
    tabCountOn: { background: ORANGE_LIGHT, color: '#a8450f' },
    tabCountUnread: { background: '#fdecea', color: '#a93226' },

    filterCard: {
        borderRadius: 14, border: `1px solid ${RULE}`,
        background: 'linear-gradient(180deg, #fcfbfa 0%, #ffffff 100%)',
        padding: '11px 14px 12px', marginBottom: 12,
    },
    filterHead: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 11 },
    filterHeadText: {
        fontSize: 11, fontWeight: 800, letterSpacing: 0.7,
        textTransform: 'uppercase', color: '#756e68',
    },
    filterCount: { fontSize: 12, fontWeight: 700, color: '#5f6876', marginLeft: 6 },
    filterRow: { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' },
    filterDot: { width: 8, height: 8, borderRadius: '50%', display: 'inline-block', marginRight: 9, flexShrink: 0 },
    filterArrow: { color: '#9aa5b1', fontSize: 15, marginTop: 2 },
    quickChip: {
        height: 26, fontSize: 11.5, fontWeight: 700,
        background: '#fff', color: '#5f6876', border: `1px solid ${RULE}`,
        '&:hover': { background: ORANGE_LIGHT, color: ORANGE_DARK, borderColor: '#f3c9b2' },
    },
    quickChipOn: {
        background: ORANGE_DARK, color: '#fff', borderColor: ORANGE_DARK,
        '&:hover': { background: ORANGE_DARK, color: '#fff' },
    },
    // Quiet: these sit inside empty states where they are the only thing to
    // click, so they do not need to compete.
    clearBtn: { borderRadius: 20, textTransform: 'none', fontWeight: 600, color: ORANGE_DARK, marginLeft: 'auto' },
    // Loud: this one sits in a row of six controls and only appears when
    // something is filtered, so it has to read as a button rather than as
    // more furniture.
    clearFiltersBtn: {
        marginLeft: 'auto',
        borderRadius: 20, textTransform: 'none', fontWeight: 800, fontSize: 12,
        padding: '4px 14px', lineHeight: 1.6,
        color: '#a8450f', background: ORANGE_LIGHT,
        border: `1px solid ${ORANGE}`,
        boxShadow: '0 2px 6px rgba(93,55,28,0.12)',
        transition: 'background-color 160ms ease, color 160ms ease, box-shadow 160ms ease',
        '&:hover': {
            background: ORANGE_DARK, color: '#fff',
            boxShadow: '0 4px 10px rgba(93,55,28,0.22)',
        },
        // Fades in with the filters rather than appearing out of nowhere.
        animation: '$clearIn 220ms ease',
    },
    '@keyframes clearIn': {
        from: { opacity: 0, transform: 'translateY(-3px)' },
        to: { opacity: 1, transform: 'none' },
    },

    // A list, not a table: these are messages, and a message wants a line of
    // body text more than it wants six aligned columns.
    row: {
        display: 'flex', alignItems: 'flex-start', gap: 13,
        padding: '13px 14px', borderRadius: 13, marginBottom: 8,
        border: `1px solid ${RULE}`, background: '#fff',
        cursor: 'pointer', position: 'relative',
        transition: 'transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease',
        '&:hover': {
            transform: 'translateY(-1px)',
            boxShadow: '0 6px 16px rgba(93,55,28,0.10)',
            borderColor: '#f3c9b2',
        },
    },
    // Unread carries a tint AND a category-coloured bar, so the two states
    // differ in weight rather than only in hue.
    rowUnread: { background: '#fffdfb' },
    rowDot: { width: 9, height: 9, borderRadius: '50%', marginTop: 7, flexShrink: 0 },
    rowBody: { minWidth: 0, flex: 1 },
    rowTop: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
    catChip: {
        height: 20, fontSize: 10, fontWeight: 800, letterSpacing: 0.4,
        textTransform: 'uppercase', borderRadius: 999,
    },
    rowTitle: { fontSize: 14, fontWeight: 700, color: INK, lineHeight: 1.35 },
    rowTitleRead: { fontWeight: 600, color: '#5f574f' },
    // Quill writes <p>/<ul>, whose margins would double the clamp height.
    rowText: {
        fontSize: 12.5, color: '#5f6876', marginTop: 4, lineHeight: 1.5,
        display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
        '& p, & ul, & ol, & h1, & h2, & h3': { margin: 0 },
        '& ul, & ol': { paddingLeft: 18 },
    },
    rowMeta: { display: 'flex', alignItems: 'center', gap: '4px 12px', flexWrap: 'wrap', marginTop: 7 },
    rowMetaItem: { fontSize: 11.5, color: '#636c7a', display: 'inline-flex', alignItems: 'center', gap: 4 },
    rowAvatar: { width: 20, height: 20, fontSize: 10, fontWeight: 700, background: ORANGE_LIGHT, color: '#a8450f' },
    filePill: {
        display: 'inline-flex', alignItems: 'center', gap: 3,
        fontSize: 10.5, fontWeight: 700, color: '#a8450f',
        background: ORANGE_LIGHT, borderRadius: 999, padding: '1px 8px',
    },
    newTag: {
        fontSize: 9.5, fontWeight: 800, letterSpacing: 0.4, textTransform: 'uppercase',
        color: '#fff', background: '#c0392b', borderRadius: 999, padding: '1px 7px',
    },
    rowTime: { fontSize: 11.5, color: '#636c7a', whiteSpace: 'nowrap', flexShrink: 0, marginTop: 2 },

    emptyWrap: { textAlign: 'center', padding: '60px 20px' },
    emptyTitle: { fontSize: 15, fontWeight: 700, color: '#5f6876', marginTop: 10 },
    emptyHint: {
        fontSize: 12.5, color: '#636c7a', marginTop: 4,
        maxWidth: 420, marginLeft: 'auto', marginRight: 'auto', lineHeight: 1.6,
    },
    // The one block that scrolls.
    listWrap: {
        position: 'relative', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column',
        [theme.breakpoints.down('xs')]: { flex: 'none', display: 'block' },
        '@media (max-height: 640px)': { flex: 'none', display: 'block' },
    },
    list: {
        flex: 1, minHeight: 0, overflowY: 'auto',
        paddingTop: 10, paddingRight: 4,
        [theme.breakpoints.down('xs')]: { flex: 'none', overflow: 'visible' },
        '@media (max-height: 640px)': { flex: 'none', overflow: 'visible' },
    },
    listFade: {
        position: 'absolute', left: 0, right: 4, bottom: 0, height: 52,
        display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
        paddingBottom: 4, cursor: 'pointer', outline: 'none',
        background: 'linear-gradient(180deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.88) 55%, #fff 100%)',
        '&:hover $listFadePill': { background: ORANGE_DARK, color: '#fff' },
        '&:focus-visible $listFadePill': { boxShadow: '0 0 0 3px rgba(244,126,68,0.35)' },
    },
    listFadePill: {
        display: 'inline-flex', alignItems: 'center', gap: 3,
        fontSize: 11, fontWeight: 800, letterSpacing: 0.3,
        background: '#fff', color: ORANGE_DARK,
        border: `1px solid ${ORANGE}`, borderRadius: 999, padding: '3px 12px',
        boxShadow: '0 2px 8px rgba(93,55,28,0.16)',
        transition: 'background-color 160ms ease, color 160ms ease',
    },
    // Never squeezed - it sits below the scroller and must stay reachable,
    // which is the whole point of the fixed-height page.
    pagination: { flexShrink: 0, borderTop: `1px solid ${RULE}` },
}))

const MyNotifications = (props) => {
    const classes = useStyles()

    const [tab, setTab] = useState('all')
    const [rows, setRows] = useState([])
    const [counts, setCounts] = useState({ all: 0, unread: 0, read: 0 })
    const [total, setTotal] = useState(0)
    const [page, setPage] = useState(0)
    const [rowsPerPage, setRowsPerPage] = useState(ROWS_OPTIONS[0])

    const [search, setSearch] = useState('')
    const [categoryId, setCategoryId] = useState(ALL_CATEGORIES)
    const [from, setFrom] = useState('')
    const [to, setTo] = useState('')
    const [categories, setCategories] = useState([])

    const [loading, setLoading] = useState(true)
    const [refreshing, setRefreshing] = useState(false)
    const [markingAll, setMarkingAll] = useState(false)
    const [loadError, setLoadError] = useState('')
    const [detail, setDetail] = useState(null)

    // How many rows are still below the fold of the internal scroller.
    const listRef = useRef(null)
    const [hiddenBelow, setHiddenBelow] = useState(0)

    const load = useCallback(async (opts = {}) => {
        if (opts.silent) setRefreshing(true); else setLoading(true)
        try {
            const res = await HttpService.getMyNotifications({
                status: tab,
                limit: rowsPerPage,
                skip: page * rowsPerPage,
                categoryId: categoryId === ALL_CATEGORIES ? undefined : categoryId,
                from: from || undefined,
                to: to || undefined,
            })
            setRows(res.data.notifications || [])
            setTotal(Number(res.data.totalCount) || 0)
            setCounts({
                all: Number(res.data.allCount) || 0,
                unread: Number(res.data.unreadCount) || 0,
                read: Number(res.data.readCount) || 0,
            })
            setLoadError('')
        } catch (err) {
            setRows([])
            setTotal(0)
            // An empty list after a failed call reads exactly like "nothing was
            // ever sent to you", which is the opposite of the truth.
            setLoadError(err?.response?.data?.message || err?.message || 'Could not load your notifications.')
        }
        setRefreshing(false)
        setLoading(false)
    }, [tab, page, rowsPerPage, categoryId, from, to])

    const measureList = useCallback(() => {
        const el = listRef.current
        if (!el) { setHiddenBelow(0); return }
        const remaining = el.scrollHeight - el.scrollTop - el.clientHeight
        if (remaining <= 4) { setHiddenBelow(0); return }
        // Rows, not pixels: "3 more" means something, "212px more" does not.
        const rowHeight = el.scrollHeight / Math.max(el.children.length, 1)
        setHiddenBelow(Math.max(1, Math.round(remaining / Math.max(rowHeight, 1))))
    }, [])

    useEffect(() => { load() }, [load])

    useEffect(() => {
        HttpService.getNotificationCategories()
            .then((res) => setCategories(res.data.categories || []))
            .catch(() => { /* the filter just stays on "All categories" */ })
    }, [])

    // Re-measured when the list changes, not only on scroll: a new page or a
    // filter changes what is below the fold.
    useEffect(() => {
        const t = setTimeout(measureList, 0)
        return () => clearTimeout(t)
    }, [rows, loading, search, measureList])

    // Narrowing the list leaves you parked on a page that no longer exists.
    useEffect(() => { setPage(0) }, [tab, categoryId, from, to, rowsPerPage])

    // Search is client-side over the page in hand, deliberately: the server
    // has no text index on these, and a regex scan per keystroke across every
    // recipient row is a worse trade than filtering what is already here.
    const term = search.trim().toLowerCase()
    const visible = term
        ? rows.filter((n) => [n.title, n.message, n.categoryName, n.sentBy]
            .some((v) => String(v || '').toLowerCase().includes(term)))
        : rows

    const openOne = async (n) => {
        setDetail(n)
        if (n.readAt) return
        // Optimistic: waiting on the round trip before the dot clears makes
        // the click feel broken.
        setRows((prev) => prev.map((r) => (r._id === n._id ? { ...r, readAt: new Date().toISOString() } : r)))
        setCounts((c) => ({ ...c, unread: Math.max(0, c.unread - 1), read: c.read + 1 }))
        try {
            await HttpService.markNotificationRead(n._id)
            // The Unread tab must drop the row it no longer matches.
            if (tab === 'unread') load({ silent: true })
        } catch (err) {
            props.setSnackInfo('Could not mark that as read', 'error')
        }
    }

    const markAllRead = async () => {
        setMarkingAll(true)
        try {
            await HttpService.markAllNotificationsRead()
            props.setSnackInfo('All caught up', 'success')
            await load({ silent: true })
        } catch (err) {
            props.setSnackInfo(err?.response?.data?.message || 'Could not mark everything read', 'error')
        }
        setMarkingAll(false)
    }

    const busy = loading || refreshing
    const activeFilterCount =
        (search.trim() ? 1 : 0)
        + (categoryId !== ALL_CATEGORIES ? 1 : 0)
        // From and To are one filter, not two - they describe a single range.
        + ((from || to) ? 1 : 0)
    const hasFilters = activeFilterCount > 0
    const clearFilters = () => { setSearch(''); setCategoryId(ALL_CATEGORIES); setFrom(''); setTo('') }

    const renderEmpty = () => {
        if (loadError) {
            return (
                <div className={classes.emptyWrap}>
                    <ErrorOutlineIcon style={{ fontSize: 50, color: '#e8a49c' }} />
                    <div className={classes.emptyTitle}>Could not load your notifications</div>
                    <div className={classes.emptyHint}>{loadError}</div>
                    <Button size="small" className={classes.clearBtn} style={{ marginTop: 12, marginLeft: 0 }}
                        startIcon={<RefreshIcon />} onClick={() => load()}>
                        Try again
                    </Button>
                </div>
            )
        }
        if (hasFilters) {
            return (
                <div className={classes.emptyWrap}>
                    <SearchOffIcon style={{ fontSize: 50, color: '#c9d2de' }} />
                    <div className={classes.emptyTitle}>Nothing matches this view</div>
                    <div className={classes.emptyHint}>
                        You have {counts.all} notification{counts.all === 1 ? '' : 's'} in total. Try clearing the filters.
                    </div>
                    <Button size="small" className={classes.clearBtn} style={{ marginTop: 12, marginLeft: 0 }}
                        startIcon={<ClearIcon />} onClick={clearFilters}>
                        Clear filters
                    </Button>
                </div>
            )
        }
        if (tab === 'unread') {
            return (
                <div className={classes.emptyWrap}>
                    <DoneAllIcon style={{ fontSize: 50, color: '#b7e0c4' }} />
                    <div className={classes.emptyTitle}>You are all caught up</div>
                    <div className={classes.emptyHint}>Nothing unread. Anything new will appear here and on the bell.</div>
                </div>
            )
        }
        if (tab === 'read') {
            return (
                <div className={classes.emptyWrap}>
                    <DraftsIcon style={{ fontSize: 50, color: '#c9d2de' }} />
                    <div className={classes.emptyTitle}>Nothing read yet</div>
                    <div className={classes.emptyHint}>Notifications move here once you open them.</div>
                </div>
            )
        }
        return (
            <div className={classes.emptyWrap}>
                <NotificationsNoneIcon style={{ fontSize: 50, color: '#c9d2de' }} />
                <div className={classes.emptyTitle}>No notifications yet</div>
                <div className={classes.emptyHint}>Anything sent to you shows up here and on the bell in the header.</div>
            </div>
        )
    }

    return (
        <div className={classes.root}>
            <div className={classes.banner}>
                <div className={classes.bannerIcon}><NotificationsActiveIcon style={{ fontSize: 24 }} /></div>
                <div style={{ minWidth: 0 }}>
                    <div className={classes.bannerTitle}>My Notifications</div>
                    <div className={classes.bannerSub}>
                        Everything that has been sent to you.
                    </div>
                </div>
                <div className={classes.bannerActions}>
                    <Button className={classes.whiteBtn} size="small" disabled={busy}
                        startIcon={<RefreshIcon className={refreshing ? classes.spin : undefined} />}
                        onClick={() => load({ silent: true })}>
                        {refreshing ? 'Refreshing' : 'Refresh'}
                    </Button>
                    <Button className={classes.whiteBtn} size="small"
                        disabled={markingAll || busy || counts.unread === 0}
                        startIcon={markingAll
                            ? <CircularProgress size={14} thickness={5} style={{ color: ORANGE_DARK }} />
                            : <DoneAllIcon />}
                        onClick={markAllRead}>
                        {markingAll ? 'Marking…' : `Mark all read${counts.unread ? ` (${counts.unread})` : ''}`}
                    </Button>
                </div>
            </div>

            <div className={classes.progressSlot}>{busy ? <BrandLinearProgress /> : null}</div>

            {busy && (
                <div className={classes.loadingNote}>
                    <CircularProgress size={15} thickness={5} style={{ color: ORANGE_DARK }} />
                    <div>
                        <div className={classes.loadingNoteTitle}>
                            {refreshing ? 'Refreshing your notifications…' : 'Fetching your notifications…'}
                        </div>
                        {/* Names the tab, the window and any filter, because a
                            bare spinner does not explain why the list is about
                            to look different from the one you were reading. */}
                        <div className={classes.loadingNoteHint}>
                            {TABS.find((t) => t.key === tab).label} · page {page + 1}, {rowsPerPage} per page
                            {categoryId !== ALL_CATEGORIES
                                ? ` · ${categories.find((c) => c._id === categoryId)?.name || 'one category'}`
                                : ''}
                            {(from || to)
                                ? ` · ${from ? moment(from).format('DD MMM') : 'the start'} to ${to ? moment(to).format('DD MMM') : 'today'}`
                                : ''}
                        </div>
                    </div>
                </div>
            )}

            <div className={classes.card}>
                {/* Tabs and filters stay put while the list scrolls under
                    them: with 40 notifications you otherwise lose both the
                    tab you are in and the way back to the filters. */}
                <div className={classes.stickyHead}>
                <Tabs
                    value={tab}
                    onChange={(e, v) => setTab(v)}
                    className={classes.tabs}
                    classes={{ indicator: classes.tabIndicator }}
                    variant="scrollable"
                    scrollButtons="auto"
                >
                    {TABS.map((t) => {
                        const TabIcon = t.Icon
                        const n = counts[t.key] || 0
                        return (
                            <Tab
                                key={t.key}
                                value={t.key}
                                className={classes.tab}
                                label={
                                    <>
                                        <TabIcon style={{ fontSize: 17 }} />
                                        {t.label}
                                        <span className={[
                                            classes.tabCount,
                                            tab === t.key ? classes.tabCountOn : '',
                                            t.key === 'unread' && n > 0 ? classes.tabCountUnread : '',
                                        ].filter(Boolean).join(' ')}>
                                            {n}
                                        </span>
                                    </>
                                }
                            />
                        )
                    })}
                </Tabs>

                <div className={classes.filterCard}>
                    <div className={classes.filterHead}>
                        <FilterListIcon style={{ fontSize: 16, color: ORANGE_DARK }} />
                        <span className={classes.filterHeadText}>Filters</span>
                        <span className={classes.filterCount}>
                            {busy ? 'Loading…' : `${total} in ${TABS.find((t) => t.key === tab).label.toLowerCase()}`}
                        </span>
                        {hasFilters && (
                            <Tooltip title="Remove all filters and show everything in this tab">
                                <Button
                                    size="small"
                                    className={classes.clearFiltersBtn}
                                    startIcon={<ClearIcon style={{ fontSize: 15 }} />}
                                    onClick={clearFilters}
                                >
                                    {/* Says what it will undo. A bare "Clear"
                                        beside a row of controls reads as part
                                        of the furniture; a count does not. */}
                                    Clear {activeFilterCount} filter{activeFilterCount === 1 ? '' : 's'}
                                </Button>
                            </Tooltip>
                        )}
                    </div>

                    <div className={classes.filterRow}>
                        <TextField
                            size="small" variant="outlined" placeholder="Search title, message or sender"
                            value={search} onChange={(e) => setSearch(e.target.value)}
                            style={{ minWidth: 250, flex: '0 1 320px' }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon style={{ fontSize: 18, color: '#9aa5b1' }} />
                                    </InputAdornment>
                                ),
                                endAdornment: search ? (
                                    <InputAdornment position="end">
                                        <IconButton size="small" onClick={() => setSearch('')} aria-label="clear">
                                            <ClearIcon style={{ fontSize: 15 }} />
                                        </IconButton>
                                    </InputAdornment>
                                ) : null,
                            }}
                        />
                        <TextField
                            select size="small" variant="outlined" label="Category"
                            value={categoryId} onChange={(e) => setCategoryId(e.target.value)}
                            style={{ minWidth: 180 }}
                        >
                            <MenuItem value={ALL_CATEGORIES}><em>All categories</em></MenuItem>
                            {categories.map((c) => (
                                <MenuItem key={c._id} value={c._id}>
                                    <span className={classes.filterDot} style={{ background: (toneOf({ categoryTone: c.tone })).color }} />
                                    {c.name}
                                </MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            type="date" size="small" variant="outlined" label="From"
                            value={from} onChange={(e) => setFrom(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            inputProps={{ max: to || isoDay(new Date()) }}
                            style={{ width: 155 }}
                        />
                        <span className={classes.filterArrow}>&rarr;</span>
                        <TextField
                            type="date" size="small" variant="outlined" label="To"
                            value={to} onChange={(e) => setTo(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            inputProps={{ min: from || undefined, max: isoDay(new Date()) }}
                            style={{ width: 155 }}
                        />
                        {QUICK_RANGES.map((r) => {
                            const range = r.range()
                            const active = from === range.from && to === range.to
                            return (
                                <Chip
                                    key={r.label} size="small" clickable label={r.label}
                                    className={`${classes.quickChip} ${active ? classes.quickChipOn : ''}`}
                                    onClick={() => {
                                        if (active) { setFrom(''); setTo(''); return }
                                        setFrom(range.from); setTo(range.to)
                                    }}
                                />
                            )
                        })}
                    </div>
                </div>
                </div>

                <div className={classes.listWrap}>
                <div className={classes.list} ref={listRef} onScroll={measureList}>
                {loading ? (
                    [...Array(5)].map((_, i) => <Skeleton key={i} height={74} style={{ marginBottom: 6 }} />)
                ) : visible.length === 0 ? (
                    renderEmpty()
                ) : (
                    visible.map((n) => {
                        const tone = toneOf(n)
                        const unread = !n.readAt
                        const files = n.attachments || []
                        return (
                            <div
                                key={n._id}
                                role="button"
                                tabIndex={0}
                                className={`${classes.row} ${unread ? classes.rowUnread : ''}`}
                                style={unread ? { boxShadow: `inset 3px 0 0 0 ${tone.color}` } : undefined}
                                onClick={() => openOne(n)}
                                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openOne(n) } }}
                            >
                                <span className={classes.rowDot} style={{ background: unread ? tone.color : '#d6d2cf' }} />
                                <span className={classes.rowBody}>
                                    <span className={classes.rowTop}>
                                        {/* Same tone the Sent tab and the bell use, from the
                                            snapshot on the row - so a category renamed later
                                            still reads as it did when this was sent. */}
                                        {n.categoryName && (
                                            <Chip size="small" className={classes.catChip} label={n.categoryName}
                                                style={{ background: tone.soft, color: tone.deep }} />
                                        )}
                                        <span className={`${classes.rowTitle} ${unread ? '' : classes.rowTitleRead}`}>
                                            {n.title}
                                        </span>
                                        {unread && <span className={classes.newTag}>New</span>}
                                        {n.link && <OpenInNewIcon style={{ fontSize: 13, color: ORANGE_DARK }} />}
                                    </span>

                                    {n.messageHtml
                                        ? <span className={classes.rowText} dangerouslySetInnerHTML={{ __html: n.messageHtml }} />
                                        : <span className={classes.rowText}>{n.message}</span>}

                                    <span className={classes.rowMeta}>
                                        <span className={classes.rowMetaItem}>
                                            <Avatar className={classes.rowAvatar}>
                                                {String(n.sentBy || '?').trim().charAt(0).toUpperCase()}
                                            </Avatar>
                                            {n.sentBy || 'Decorpot ERP'}
                                        </span>
                                        <span className={classes.rowMetaItem}>
                                            <AccessTimeIcon style={{ fontSize: 12 }} />
                                            {moment(n.sentAt).format('DD MMM YYYY, h:mm A')}
                                        </span>
                                        {files.length > 0 && (
                                            <span className={classes.filePill}>
                                                <AttachFileIcon style={{ fontSize: 11 }} />
                                                {files.length}
                                            </span>
                                        )}
                                        {n.readAt && (
                                            <span className={classes.rowMetaItem} style={{ color: '#19692c' }}>
                                                <DoneAllIcon style={{ fontSize: 13 }} />
                                                read {moment(n.readAt).fromNow()}
                                            </span>
                                        )}
                                    </span>
                                </span>
                                <span className={classes.rowTime}>{moment(n.sentAt).fromNow()}</span>
                            </div>
                        )
                    })
                )}
                </div>
                {/* Fades the last visible row instead of cutting it off square,
                    which is what made the list look like it ended there. */}
                {hiddenBelow > 0 && (
                    <div
                        role="button"
                        tabIndex={0}
                        aria-label={`Scroll to see ${hiddenBelow} more`}
                        className={classes.listFade}
                        onClick={() => listRef.current && listRef.current.scrollBy({ top: 240, behavior: 'smooth' })}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); listRef.current && listRef.current.scrollBy({ top: 240, behavior: 'smooth' }) } }}
                    >
                        <span className={classes.listFadePill}>
                            <KeyboardArrowDownIcon style={{ fontSize: 15 }} />
                            {hiddenBelow} more below
                        </span>
                    </div>
                )}
                </div>

                {!loading && total > 0 && (
                    <TablePagination
                        className={classes.pagination}
                        component="div"
                        count={total}
                        page={page}
                        onChangePage={(e, p) => setPage(p)}
                        rowsPerPage={rowsPerPage}
                        onChangeRowsPerPage={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0) }}
                        rowsPerPageOptions={ROWS_OPTIONS}
                    />
                )}
            </div>

            {/* The same full-page sheet the bell opens, so a notification reads
                identically wherever it was opened from. */}
            <NotificationDetailSheet notification={detail} onClose={() => setDetail(null)} />
        </div>
    )
}

export default connect(null, { setSnackInfo })(MyNotifications)
